import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'config/axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
// import { GoArrowLeft } from "react-icons/go";
import './index.css';

import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Header from 'components/Header';
import InfoBox from 'components/InfoBox';
import LevelBar from 'components/LevelBar';
import Loader from 'components/Loader';
import Button from 'components/Button';
import Dropdown3 from 'components/Dropdown3';
    
const TEST_TYPE = ['SAT', 'ACT', 'SHSAT']
const SAT_TEST_TYPE = ['ACT', 'SAT']
const SHSAT_TEST_TYPE = ['ACT', 'SHSAT']
const ACT_TEST_TYPE = ['ACT']

function HistoricalACTReport() {
    const [testData, setTestData] = useState(null);
    const [isSATActive, setIsSATActive] = useState(false);
    const [isSHSATActive, setIsSHSATActive] = useState(false);
    const [studentName, setStudentName] = useState('');
    const [organization, setOrganization] = useState('');
    const [since, setSince] = useState('');
    const [tests, setTests] = useState([]);
    const [last10Tests, setLast10Tests] = useState([]);
    const [allTests, setAllTests] = useState([]);
    const [lastTest, setLastTest] = useState(null);
    const [modules, setModules] = useState([]);
    const [englishRootCategories, setEnglishRootCategories] = useState([]);
    const [mathRootCategories, setMathRootCategories] = useState([]);
    const [readingRootCategories, setReadingRootCategories] = useState([]);
    const [scienceRootCategories, setScienceRootCategories] = useState([]);
    const [avgCorrectPercent, setAvgCorrectPercent] = useState({});
    const [avgAllTests, setAvgAllTests] = useState(0);
    const [avgEnglishTests, setAvgEnglishTests] = useState(0);
    const [avgMathTests, setAvgMathTests] = useState(0);
    const [avgReadingTests, setAvgReadingTests] = useState(0);
    const [avgScienceTests, setAvgScienceTests] = useState(0);
    const [avgTestDuration, setAvgTestDuration] = useState(0);
    // const [avgPercentile, setAvgPercentile] = useState(0);
    // const [totalMaxScore, setTotalMaxScore]=useState(0);
    // const [totalMinScore, setTotalMinScore] = useState(0);
    const [totalScoreRange, setTotalScoreRange] = useState([]);
    const [englishScoreRange, setEnglishScoreRange] = useState([]);
    const [mathScoreRange, setMathScoreRange] = useState([]);
    const [readingScoreRange, setReadingScoreRange] = useState([]);
    const [scienceScoreRange, setScienceScoreRange] = useState([]);
    const [fontSize, setFontSize] = useState(12);
    const [smallFontSize, setSmallFontSize] = useState(0);
    const [scrollStandard, setScrollStandard] = useState(10);
    const [testType, setTestType] = useState(TEST_TYPE[1]);
    const navigate = useNavigate();
    const { studentId } = useParams();
    const scrollRef = useRef(null); 
    const printRef = useRef(null);

    const CustomTooltip = ({ payload, active }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload; // 막대의 데이터
            const testDate = new Date(data.testDate).toLocaleDateString();
            const englishScore = Math.round(data.englishScore);
            const mathScore = Math.round(data.mathScore);
            const readingScore = Math.round(data.readingScore);
            const scienceScore = Math.round(data.scienceScore);
            // const totalScore = data.totalScore;
            const compositeScore = Math.round(data.compositeScore);
            const stemScore = Math.round(data.stemScore);
    
            return (
                <div className="custom-tooltip">
                    <p>Test Date: {testDate}</p>
                    {englishScore>0&&<p style={{ color: '#82ca9d' }}>English: {englishScore}</p>}
                    {mathScore>0&&<p style={{ color: '#82ca9d' }}>Math: {mathScore}</p>}
                    {readingScore>0&&<p style={{ color: '#82ca9d' }}>Reading: {readingScore}</p>}
                    {scienceScore>0&&<p style={{ color: '#82ca9d' }}>Science: {scienceScore}</p>}
                    {compositeScore>0&&<p style={{ color: '#1D2A57', fontWeight:'bold', fontSize: '12'}}>Composite: {compositeScore}</p>}
                    {stemScore>0&&<p style={{ color: '#2DBE45', fontWeight:'bold', fontSize: '12'}}>STEM: {stemScore}</p>}
                </div>
            );
        }
        return null;
    }

    
    const CustomBar = ({ x, y, width, height, isNull, move, subject, value }) => {
        if (isNull) return null;
        const upperHeight = 6; // 위쪽 부분의 높이
        const barWidth = isNull ? 0 : width;
        // const adjustedX = move ? x + width : x;
        const adjustedX = x + (width*move)
        const textX = adjustedX + width / 2 + 2; 
        const textY = y - smallFontSize / 2; 
    
        return (
            <g>
                {/* 바 렌더링 */}
                <rect x={adjustedX} y={y} width={barWidth} height={upperHeight} fill={`var(--deepGreen)`} />
                <rect x={adjustedX} y={y + upperHeight} width={barWidth} height={height - upperHeight} fill="#E8E8E8" />
                
                {/* 텍스트 렌더링 */}
                {!isNull && value !== null && value !== undefined && (
                    <text 
                        x={textX} 
                        y={textY} 
                        fill="#000" 
                        fontSize={smallFontSize} 
                        fontWeight={400} 
                        textAnchor="start" 
                        transform={`rotate(270, ${textX}, ${textY})`} 
                    >
                        {subject} <tspan fontWeight={700}>{value}</tspan>
                    </text>
                )}
            </g>
        );
    };

    const CustomBarPrint = ({ x, y, width, height, isNull, move, subject, value }) => {
        if (isNull) return null;
        const upperHeight = 6; // 위쪽 부분의 높이
        const barWidth = isNull ? 0 : width;
        // const adjustedX = move ? x + width : x;
        const adjustedX = x + (width*move)
        const textX = adjustedX + width / 2 + 2; 
        const textY = y - smallFontSize / 2; 
    
        return (
            <g>
                {/* 바 렌더링 */}
                <rect x={adjustedX} y={y} width={barWidth} height={upperHeight} fill={`var(--deepGreen)`} />
                <rect x={adjustedX} y={y + upperHeight} width={barWidth} height={height - upperHeight} fill="#E8E8E8" />
                
                {/* 텍스트 렌더링 */}
                {!isNull && value !== null && value !== undefined && (
                    <text 
                        x={textX} 
                        y={textY} 
                        fill="#000" 
                        fontSize={smallFontSize*2} 
                        fontWeight={400} 
                        textAnchor="start" 
                        transform={`rotate(270, ${textX}, ${textY})`} 
                    >
                        {subject} <tspan fontWeight={700}>{value}</tspan>
                    </text>
                )}
            </g>
        );
    };
    
    
    const CustomTick = ({ x, y, payload, fontSize, fontWeight, fill }) => {
        const lines = payload.value.split('\n');
        const marginAbove = 10;
        return (
            <g transform={`translate(${x},${y + marginAbove})`}>
            <text 
              x={0} 
              y={0} 
              dy={16} 
              textAnchor="middle" 
              fill={fill} 
              fontSize={fontSize} 
              fontWeight={fontWeight}
            >
              {lines.map((line, index) => (
                <tspan key={index} x="0" dy={index === 0 ? 0 : 16}>{line}</tspan>
              ))}
            </text>
          </g>
        );
      };

    const handlePrint = useReactToPrint({
        content: () => printRef.current
    });

    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth <= 768) {
          setFontSize(10); 
          setSmallFontSize(8);
        } else {
          setFontSize(12); 
          setSmallFontSize(10);
        }

        if(window.innerWidth <= 480) {
            setScrollStandard(3);
        } else if (window.innerWidth <= 576) {
            setScrollStandard(4);
        } else if(window.innerWidth <= 768){
            setScrollStandard(6);
        } else{
            setScrollStandard(10);
        }
      };
  
      window.addEventListener('resize', handleResize);
      handleResize(); 
      return () => window.removeEventListener('resize', handleResize); 
    }, [scrollStandard]);

    useEffect(()=>{
        axios.get(`tutors/digital/act/historicalResult/${studentId}/`)
        .then(res => {
            setTestData(res.data);
            setIsSATActive(res.data.isSATActiveHistoricalReport);
            setIsSHSATActive(res.data.isSHSATActiveHistoricalReport);
            setStudentName(res.data.studentName);
            setOrganization(res.data.organization);
            setSince(res.data.since);
            setTests(res.data.last5tests);
            setLast10Tests(res.data.last10Tests);
            setAllTests(res.data.allTests);
            setLastTest(res.data.lastTest);
            setModules(res.data.lastTest.testModules);
            // setRwRootCategories(res.data.lastTest.testSections.find(section=>section.subject==="Reading and Writing").rootCategories)
            // setMathRootCategories(res.data.lastTest.testSections.find(section=>section.subject==="Math").rootCategories)
            setEnglishRootCategories(res.data.englishRootCategories);
            setMathRootCategories(res.data.mathRootCategories);
            setReadingRootCategories(res.data.readingRootCategories);
            setScienceRootCategories(res.data.scienceRootCategories);
            // setTestSections(res.data.lastTest.testSections);
            setAvgCorrectPercent(res.data.avgCorrectPercent);
            setAvgAllTests(res.data.avgAllTests);
            setAvgEnglishTests(res.data.avgEnglishTests);
            setAvgMathTests(res.data.avgMathTests);
            setAvgReadingTests(res.data.avgReadingTests);
            setAvgScienceTests(res.data.avgScienceTests);
            // setAvgPercentile(res.data.avgPercentile);
            setAvgTestDuration(res.data.avgTestDuration);
            setTotalScoreRange(res.data.totalScoreRange);
            setEnglishScoreRange(res.data.englishScoreRange);
            setMathScoreRange(res.data.mathScoreRange);
            setReadingScoreRange(res.data.readingScoreRange);
            setScienceScoreRange(res.data.scienceScoreRange);
            // setTotalMaxScore(parseInt(res.data.totalMaxScore));
            // setTotalMinScore(parseInt(res.data.totalMinScore));
        })
        .catch(error => {
            console.log(error);
        });
    }, [testType]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.scrollWidth; 
        }
    }, [allTests]);

    const formatDuration = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        let result = '';

        if (minutes>0){
            result+=`${minutes}m `;
        }
        if (seconds>0){
            result+=`${seconds}s`;
        }
        return result;
    };

    const formatDurationSecOnly = (totalSeconds) => {
        const minutes = Math.round(totalSeconds / 60);
        return `${minutes} min`;
    };

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    Performance History (ACT) - Test Center Admin
                </title>
            </Helmet>
            {tests.length>0?
                <div className='historicalReportACT__wrap'>
                    <Header
                        title={'Performance History'}
                        prevRoutes={[{ name: 'All Students', path: '/students' },
                                    { name: `${studentName}`, path: `/students/${studentId}` }
                        ]}
                    />
                    <div className='historicalReportACT__print'>
                        <Button
                            onClick={handlePrint}
                        >
                            Print Report
                        </Button>
                        <Dropdown3
                            data={(isSATActive && isSHSATActive)
                                ? TEST_TYPE
                                : isSATActive 
                                ? SAT_TEST_TYPE  
                                : isSHSATActive
                                ? SHSAT_TEST_TYPE
                                : ACT_TEST_TYPE
                            }
                            value={testType}
                            onSelect={value => {
                                if (value === 'SAT') {
                                    // setTestType(value);
                                    navigate(`/students/${studentId}/digital-sat/performanceHistory`);
                                } else if (value === 'SHSAT') {
                                    navigate(`/students/${studentId}/digital-shsat/performanceHistory`);
                                }
                            }}
                        />
                    </div>  
                    {/* Report main */}
                    <div className='historicalReportACT__report' ref={printRef}>
                        <div className='historicalReportACT__headerRowInfo'>
                            <div className='historicalReportACT__headerSection'>
                                {testData.logo && 
                                    <img
                                        className='historicalReportACT__headerLogo'
                                        alt='logo'
                                        src={testData.logo}
                                    />
                                }
                            </div>
                            <div className='historicalReportACT__titleRight'>
                                <h1 className='historicalReportACT__title'>Performance History</h1>
                                <h2 className='historicalReportACT__studentName'>{studentName}</h2>
                                <h2 className='historicalReportACT__studentEmail'>{testData.email}</h2>    
                            </div>
                        </div>
                        {/* Basic information */}
                        <div className='historicalReportACT__reportSection'>
                            <div className='historicalReportACT__sectionTitle'>Basic Information</div>
                            <div className='historicalReportACT__sectionContent'>
                                <InfoBox title="Name" content = {studentName}/>
                                <InfoBox title="Organization" content = {organization}/>
                                <InfoBox title="Since" content = {since}/>
                                <InfoBox title="Last Test Duration" content = {formatDuration(lastTest?.testDuration)} />
                            </div>
                        </div>

                        {/* Last Test */}
                        <div className='historicalReportACT__reportSection'>
                            <div className='historicalReportACT__sectionTitleWrap'>
                                <div className='historicalReportACT__sectionTitle'>Latest Test</div>
                                <div className='historicalReportACT__type'>{lastTest?.testType}</div>
                                <div className='historicalReport__date'>{lastTest?.testDate2}</div>
                            </div>
                            <div className='historicalReportACT__sectionContent2'>
                                {/* <InfoBox middle title="Test Date" content = {lastTest?.testDate2}/> */}
                                <InfoBox className='historicalReportACT__content2Info0' big title="Composite" content = {Math.round(lastTest?.compositeScore)}/>
                                <InfoBox className='historicalReportACT__content2Info1' big title="STEM" content = {Math.round(lastTest?.stemScore)}/>
                                {/* <InfoBox big title="% Percentile" content = {Math.round(lastTest?.percentile)}/> */}
                                <InfoBox className='historicalReportACT__content2Info2' big title="English" content = {lastTest?.englishScore? Math.round(lastTest?.englishScore):'-'}/>
                                <InfoBox className='historicalReportACT__content2Info3' big title="Mathematics" content = {lastTest?.mathScore? Math.round(lastTest?.mathScore):'-'}/>
                                <InfoBox className='historicalReportACT__content2Info4' big title="Reading" content = {lastTest?.readingScore? Math.round(lastTest?.readingScore):'-'}/>
                                <InfoBox className='historicalReportACT__content2Info5' big title="Science" content = {lastTest?.scienceScore? Math.round(lastTest?.scienceScore):'-'}/>
                                <div className={`historicalReportACT__sectionBox historicalReportACT__sectionEnglish`}>
                                    <table className='historicalReportACT__table'>
                                        <thead>
                                            <tr className='historicalReportACT__tableHeader'>
                                                <th className='historicalReportACT__tableRowName'>Score by English</th>
                                                <th># Correct</th>
                                                <th># Total</th>
                                                <th>% Percent</th>
                                            </tr>
                                        </thead>
                                        <tr className="historicalReportACT__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {lastTest.testSections.find(section=>section.subject==="ACT English")?.rootCategories &&
                                        Object.entries(lastTest.testSections.find(section=>section.subject==="ACT English").rootCategories)?.map(([category, data],i)=>(
                                            <tr>
                                                <td className='historicalReportACT__tableRowName'>{category || '-'}</td>
                                                <td>{data && data['numCorrect'] !== undefined ? data['numCorrect'] : '-'}</td>
                                                <td>{data && data['numQuestions'] !== undefined ? data['numQuestions'] : '-'}</td>
                                                <td>
                                                    {data && data['numQuestions'] > 0 
                                                        ? `${Math.round((data['numCorrect'] / data['numQuestions']) * 100)}%` 
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </table>
                                </div>
                                <div className={lastTest.testSections.find(section=>section.subject==="ACT Science")
                                    ? `historicalReportACT__sectionBox historicalReportACT__sectionMath--science`
                                    : `historicalReportACT__sectionBox historicalReportACT__sectionMath`
                                }>
                                    <table className='historicalReportACT__table'>
                                        <thead>
                                            <tr className='historicalReportACT__tableHeader'>
                                                <th className='historicalReportACT__tableRowName'>Score by Math</th>
                                                <th># Correct</th>
                                                <th># Total</th>
                                                <th>% Percent</th>
                                            </tr>
                                        </thead>
                                        <tr className="historicalReportACT__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {lastTest.testSections.find(section=>section.subject==="ACT Mathematics")?.rootCategories&&
                                        Object.entries(lastTest.testSections.find(section=>section.subject==="ACT Mathematics").rootCategories)?.map(([category, data],i)=>(
                                            <tr>
                                                <td className='historicalReportACT__tableRowName'>{category || '-'}</td>
                                                <td>{data && data['numCorrect'] !== undefined ? data['numCorrect'] : '-'}</td>
                                                <td>{data && data['numQuestions'] !== undefined ? data['numQuestions'] : '-'}</td>
                                                <td>
                                                    {data && data['numQuestions'] > 0 
                                                        ? `${Math.round((data['numCorrect'] / data['numQuestions']) * 100)}%` 
                                                        : '-'}
                                                </td>                                            
                                            </tr>
                                        ))}
                                    </table>
                                </div>
                                <div className={`historicalReportACT__sectionBox historicalReportACT__sectionReading`}>
                                    <table className='historicalReportACT__table'>
                                        <thead>
                                            <tr className='historicalReportACT__tableHeader'>
                                                <th className='historicalReportACT__tableRowName'>Score by Reading</th>
                                                <th># Correct</th>
                                                <th># Total</th>
                                                <th>% Percent</th>
                                            </tr>
                                        </thead>
                                        <tr className="historicalReportACT__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {lastTest.testSections.find(section=>section.subject==="ACT Reading")?.rootCategories&&
                                        Object.entries(lastTest.testSections.find(section=>section.subject==="ACT Reading").rootCategories)?.map(([category, data],i)=>(
                                            <tr>
                                                <td className='historicalReportACT__tableRowName'>{category || '-'}</td>
                                                <td>{data && data['numCorrect'] !== undefined ? data['numCorrect'] : '-'}</td>
                                                <td>{data && data['numQuestions'] !== undefined ? data['numQuestions'] : '-'}</td>
                                                <td>
                                                    {data && data['numQuestions'] > 0 
                                                        ? `${Math.round((data['numCorrect'] / data['numQuestions']) * 100)}%` 
                                                        : '-'}
                                                </td>                                            
                                            </tr>
                                        ))}
                                    </table>
                                </div>
                                {lastTest.testSections.find(section=>section.subject==="ACT Science") &&
                                    <div className={`historicalReportACT__sectionBox historicalReportACT__sectionScience`}>
                                        <table className='historicalReportACT__table'>
                                            <thead>
                                                <tr className='historicalReportACT__tableHeader'>
                                                    <th className='historicalReportACT__tableRowName'>Score by Science</th>
                                                    <th># Correct</th>
                                                    <th># Total</th>
                                                    <th>% Percent</th>
                                                </tr>
                                            </thead>
                                            <tr className="historicalReportACT__tableSpacingRow">
                                                <td colSpan={4}></td> 
                                            </tr>
                                            {lastTest.testSections.find(section=>section.subject==="ACT Science")?.rootCategories&&
                                            Object.entries(lastTest.testSections.find(section=>section.subject==="ACT Science").rootCategories)?.map(([category, data],i)=>(
                                                <tr>
                                                    <td className='historicalReportACT__tableRowName'>{category || '-'}</td>
                                                    <td>{data && data['numCorrect'] !== undefined ? data['numCorrect'] : '-'}</td>
                                                    <td>{data && data['numQuestions'] !== undefined ? data['numQuestions'] : '-'}</td>
                                                    <td>
                                                        {data && data['numQuestions'] > 0 
                                                            ? `${Math.round((data['numCorrect'] / data['numQuestions']) * 100)}%` 
                                                            : '-'}
                                                    </td>                                            
                                                </tr>
                                            ))}
                                        </table>
                                    </div>
                                }
                            </div>
                        </div>

                        {/* Achievements */}
                        <div className='historicalReportACT__reportSection'>
                            <div className='historicalReportACT__sectionTitle historicalReportACT__pageBreak'>Achievements</div>
                            <div className='historicalReportACT__sectionContent3'>
                                {/* <InfoBox big title="Average % Percentile" content = {Math.round(avgPercentile)} range={[0, 100]}/> */}
                                <div  className='historicalReportACT__sectionInfo'>
                                    {/* <InfoBox big title="Average % Percentile" content = {Math.round(avgPercentile)} range={[0, 100]}/> */}
                                    <InfoBox className='historicalReportACT__content2Info1' big title="Composite" content = {Math.round(testData.compositeScore)} />
                                    <InfoBox className='historicalReportACT__content2Info1' big title="STEM" content = {Math.round(testData.stemScore)} />
                                    <InfoBox className='historicalReportACT__content2Info2' big title="English Avg" content = {Math.round(avgEnglishTests)} />
                                    <InfoBox className='historicalReportACT__content2Info3' big title="Math Avg" content = {Math.round(avgMathTests)} />
                                    <InfoBox className='historicalReportACT__content2Info4' big title="Reading Avg" content = {Math.round(avgReadingTests)} />
                                    {tests.some(test => test.testSections.some(section => section.subject === "ACT Science")) &&
                                        <InfoBox className='historicalReportACT__content2Info5' big title="Science Avg" content = {Math.round(avgScienceTests)} />
                                    }
                                </div>
                                {/* <InfoBox big title="Average Test Duration" content = {formatDurationSecOnly(avgTestDuration)}/> */}
                                {/* <InfoBox big title="Number of Tests" content = {allTests.length}/> */}
                                {/* graph */}
                                <div className={`historicalReportACT__sectionBox historicalReportACT__graph`}>
                                    <div className='historicalReportACT__SectionBoxHeader'>
                                        <div className='historicalReportACT__Sectiontitle'>Recent Tests Progress</div>
                                        <div className='historicalReportACT__graphNum historicalReportACT__textAll'>All Tests</div>
                                        <div className='historicalReportACT__graphNum historicalReportACT__textFew'>Last 10 Tests</div>
                                    </div>
                                    <div
                                        ref={scrollRef}
                                        style={{
                                        width: "100%",
                                        height: "95%",
                                        overflowX: allTests.length>scrollStandard? "auto":"hidden" 
                                        }}
                                        className='historicalReportACT__normalGraph'
                                    >
                                        <ResponsiveContainer width={allTests.length>scrollStandard? allTests.length*100:"100%"} height="95%">
                                            <BarChart
                                                data={allTests}
                                                margin={{
                                                    top: 30,
                                                    right: 0,
                                                    left: 0,
                                                    bottom: 10,
                                                }}
                                                barSize={fontSize*1.5}
                                                barCategoryGap={smallFontSize*2}
                                            >
                                                <XAxis dataKey="testInfo"
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }}
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={(props) => <CustomTick {...props} fontSize={`${fontSize}px`} fontWeight="bold" fill="var(--gray400)" />}                                                    
                                                    interval={0} // 모든 라벨을 보여줌
                                                    />
                                                <YAxis 
                                                    ticks={[0, totalScoreRange[1]]} 
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }} 
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={{ fontSize: `${fontSize}px`, fontWeight: 'bold', fill: 'var(--gray400)' }} />
                                                <Tooltip content={<CustomTooltip payload={undefined} active={undefined} />} />
                                                <Bar dataKey="englishScore" 
                                                    fill="#82ca9d" 
                                                    shape={(barProps) => {
                                                        const move = 
                                                            (barProps.mathScore === null ? 1 : 0) + 
                                                            (barProps.readingScore === null ? 1 : 0) +
                                                            (barProps.scienceScore === null ? 1 : 0);
                                                        return (
                                                            <CustomBar 
                                                                {...barProps} 
                                                                isNull={barProps.englishScore === null} 
                                                                move={move} 
                                                                subject="E" 
                                                            />
                                                        );
                                                    }}
                                                />
                                                <Bar dataKey="mathScore" 
                                                    fill="#82ca9d" 
                                                    shape={(barProps) => {
                                                        const move = 
                                                            (barProps.readingScore === null ? 1 : 0) +
                                                            (barProps.scienceScore === null ? 1 : 0);
                                                        return (
                                                            <CustomBar 
                                                                {...barProps} 
                                                                isNull={barProps.mathScore === null} 
                                                                move={move} 
                                                                subject="M" 
                                                            />
                                                        );
                                                    }}
                                                />
                                                <Bar dataKey="readingScore" 
                                                    fill="#82ca9d" 
                                                    shape={(barProps) => {
                                                        const move = 
                                                            (barProps.scienceScore === null ? 1 : 0);
                                                        return (
                                                            <CustomBar 
                                                                {...barProps} 
                                                                isNull={barProps.readingScore === null} 
                                                                move={move} 
                                                                subject="R" 
                                                            />
                                                        );
                                                    }}                                                    
                                                />
                                                <Bar 
                                                    dataKey="scienceScore" 
                                                    fill="#82ca9d" 
                                                    shape={(barProps) => (
                                                        <CustomBar 
                                                            {...barProps} 
                                                            isNull={barProps.scienceScore === null} 
                                                            move={0} 
                                                            subject="S" 
                                                        />
                                                    )}
                                                />
                                        
                                                {/* <Bar 
                                                    dataKey="CompositeScore" 
                                                    fill={`var(--deepGreen)`}
                                                    label={(props) => {
                                                        const { x, y, width, value } = props;
                                                        const textX = x + width / 2;
                                                        return (
                                                            <g>
                                                                <text x={textX} y={y} dy={-15} fill={`var(--gray6)`} fontSize={fontSize} fontWeight={700} textAnchor="middle">com</text>
                                                                <text x={textX} y={y} dy={-3} fill="#000000" fontSize={fontSize} fontWeight={700} textAnchor="middle">{value}</text>
                                                            </g>
                                                        );
                                                    }}
                                                /> */}
                                                {/* <Bar 
                                                    dataKey="StemScore" 
                                                    fill={'#FB8649'}
                                                    label={(props) => {
                                                        const { x, y, width, value } = props;
                                                        const textX = x + width / 2;
                                                        return (
                                                            <g>
                                                                <text x={textX} y={y} dy={-15} fill={`var(--gray6)`} fontSize={fontSize} fontWeight={700} textAnchor="middle">stem</text>
                                                                <text x={textX} y={y} dy={-3} fill="#000000" fontSize={fontSize} fontWeight={700} textAnchor="middle">{value}</text>
                                                            </g>
                                                        );
                                                    }}
                                                /> */}
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* printGraph */}
                                    <div
                                        style={{
                                        width: "100%",
                                        height: "100%",
                                        }}
                                        className='historicalReportACT__printGraph'
                                    >
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={last10Tests}
                                                margin={{
                                                    top: 0,
                                                    right: 0,
                                                    left: 0,
                                                    bottom: 20,
                                                }}
                                                barSize={fontSize*2}
                                                barCategoryGap={smallFontSize*2}
                                            >
                                                <XAxis dataKey="testInfo"
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }}
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={(props) => <CustomTick {...props} fontSize={`${fontSize*1.5}px`} fontWeight="bold" fill="var(--gray400)" />}                                                    
                                                    interval={0} // 모든 라벨을 보여줌
                                                    />
                                                <YAxis 
                                                    ticks={[0, totalScoreRange[1]]} 
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }} 
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={{ fontSize: `${fontSize*1.5}px`, fontWeight: 'bold', fill: 'var(--gray400)' }} />
                                                <Tooltip content={<CustomTooltip payload={undefined} active={undefined} />} />
                                                {allTests.some(test => test.englishScore !== null) && (
                                                    <Bar dataKey="englishScore" 
                                                        fill="#82ca9d"
                                                        shape={(barProps) => {
                                                            const move = 
                                                                (barProps.mathScore === null ? 1 : 0) + 
                                                                (barProps.readingScore === null ? 1 : 0) +
                                                                (barProps.scienceScore === null ? 1 : 0);
                                                            return (
                                                                <CustomBarPrint 
                                                                    {...barProps} 
                                                                    isNull={barProps.englishScore === null} 
                                                                    move={move} 
                                                                    subject="E" 
                                                                />
                                                            );
                                                        }}                                                    />
                                                )}
                                                {allTests.some(test => test.mathScore !== null) && (
                                                    <Bar dataKey="mathScore" 
                                                        fill="#82ca9d" 
                                                        shape={(barProps) => {
                                                            const move = 
                                                                (barProps.readingScore === null ? 1 : 0) +
                                                                (barProps.scienceScore === null ? 1 : 0);
                                                            return (
                                                                <CustomBarPrint 
                                                                    {...barProps} 
                                                                    isNull={barProps.mathScore === null} 
                                                                    move={move} 
                                                                    subject="M" 
                                                                />
                                                            );
                                                        }}                                                    />
                                                )}
                                                {allTests.some(test => test.readingScore !== null) && (
                                                    <Bar dataKey="readingScore" 
                                                        fill="#82ca9d" 
                                                        shape={(barProps) => {
                                                            const move = 
                                                                (barProps.scienceScore === null ? 1 : 0);
                                                            return (
                                                                <CustomBarPrint 
                                                                    {...barProps} 
                                                                    isNull={barProps.readingScore === null} 
                                                                    move={move} 
                                                                    subject="R" 
                                                                />
                                                            );
                                                        }}                                                     />
                                                )}
                                                {allTests.some(test => test.scienceScore !== null) && (
                                                    <Bar dataKey="scienceScore" 
                                                        fill="#82ca9d" 
                                                        shape={(barProps) => (
                                                            <CustomBarPrint
                                                                {...barProps} 
                                                                isNull={barProps.scienceScore === null} 
                                                                move={0} 
                                                                subject="S" 
                                                            />
                                                        )}                                                    />
                                                )}
                                                {/* {allTests.some(test => test.englishScore !== null && test.mathScore !== null && test.readingScore !== null) && (
                                                    <Bar 
                                                        dataKey="CompositeScore" 
                                                        fill={`var(--deepGreen)`}
                                                        label={(props) => {
                                                            const { x, y, width, value } = props;
                                                            const textX = x + width / 2;
                                                            return (
                                                                <g>
                                                                    <text x={textX} y={y} dy={-15} fill={`var(--gray6)`} fontSize={fontSize} fontWeight={700} textAnchor="middle">total</text>
                                                                    <text x={textX} y={y} dy={-3} fill="#000000" fontSize={fontSize} fontWeight={700} textAnchor="middle">{value}</text>
                                                                </g>
                                                            );
                                                        }}
                                                    />
                                                )}
                                                {allTests.some(test => test.mathScore !== null && test.scienceScore !== null) && (
                                                    <Bar 
                                                        dataKey="StemScore" 
                                                        fill={'#FB8649'}
                                                        label={(props) => {
                                                            const { x, y, width, value } = props;
                                                            const textX = x + width / 2;
                                                            return (
                                                                <g>
                                                                    <text x={textX} y={y} dy={-15} fill={`var(--gray6)`} fontSize={fontSize} fontWeight={700} textAnchor="middle">total</text>
                                                                    <text x={textX} y={y} dy={-3} fill="#000000" fontSize={fontSize} fontWeight={700} textAnchor="middle">{value}</text>
                                                                </g>
                                                            );
                                                        }}
                                                    />
                                                )} */}
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                </div>
                                
                                {/* storng and week point-Math */}
                                <div className={`historicalReportACT__sectionBox historicalReportACT__analysis`}>
                                    <div className='historicalReportACT__SectionBoxHeader space'>
                                        <div className='historicalReportACT__Sectiontitle'>Category Breakdown (% Correct)</div>
                                    </div>
                                    <div className='historicalReportACT__analysisWrap'>
                                        {Object.keys(avgCorrectPercent).length > 0 ? (
                                            Object.entries(avgCorrectPercent).map(([subject, categories], index) => (
                                                <div key={index} className='historicalReportACT__analysisBox'>
                                                    <div className='historicalReportACT__analysisBoxTitle'>{subject.replace('ACT ', '')}</div>
                                                    <div className='historicalReportACT__analysisContentWrap'>
                                                        {Object.entries(categories)?.map(([categoryName, percent], i) => (
                                                            <div key={i} className='historicalReportACT__categoryWrap'>
                                                                <div className='historicalReportACT__categoryTitle'>{categoryName}</div>
                                                                <LevelBar className={subject==='ACT English'
                                                                                    ? 'LevelBar__greenBox'
                                                                                    : subject==='ACT Mathematics'
                                                                                    ? 'LevelBar__orangeBox'
                                                                                    : subject==='ACT Reading'
                                                                                    ? 'LevelBar__redBox'
                                                                                    : 'LevelBar__blueBox'
                                                                                    } 
                                                                            percent = {percent}/>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))
                                        ): (
                                            <div className='historicalReportACT__analysisBox'>
                                              <div className='historicalReportACT__analysisBoxTitle'>-</div>
                                              <div className='historicalReportACT__analysisContentWrap'>
                                                <div className='historicalReportACT__categoryWrap'>
                                                  <div className='historicalReportACT__categoryTitle'>-</div>
                                                  <LevelBar className='LevelBar__defaultBox' percent={0} />
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        }
                                    </div>
                                </div>
                                
                                {/* Recent tests score breakdown */}
                                <div className={`historicalReportACT__sectionBox historicalReportACT__testAch historicalReportACT__pageBreak`}>
                                    <div className='historicalReportACT__SectionBoxHeader space'>
                                        <div className='historicalReportACT__Sectiontitle'>Recent Tests Score Breakdown</div>
                                        <div className='historicalReportACT__graphNum'>Last 5 Tests</div>
                                    </div>
                                    <table className='historicalReportACT__table'>
                                        <thead>
                                            <tr className='historicalReportACT__tableHeader'>
                                                <th className='historicalReportACT__tableRowName'>English</th>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((data, i)=>(
                                                    <th>{data.testNumber}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tr className="historicalReportACT__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {englishRootCategories?.map((category)=>(
                                            <tr>
                                                <td className='historicalReportACT__tableRowName'>{category.length>0?category:'-'}</td>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((test, j) => {
                                                    const englishSection = test.testSections.find(section => section.subject === "ACT English");
                                                    return (
                                                        <td key={j}>
                                                            {englishSection?.rootCategories[category]?.numQuestions > 0 
                                                                ? isNaN(Math.round((englishSection.rootCategories[category]?.numCorrect / englishSection.rootCategories[category]?.numQuestions) * 100)) 
                                                                    ? '-' 
                                                                    : `${Math.round((englishSection.rootCategories[category]?.numCorrect / englishSection.rootCategories[category]?.numQuestions) * 100)}%`
                                                                : '-'}
                                                        </td>
                                                    );
                                                })}
                                                {/* {tests.map((test, j) => (
                                                <td key={j}>
                                                    {test.testSections[0]?.rootCategories[category]?.numQuestions > 0 
                                                        ? isNaN(Math.round((test.testSections[0]?.rootCategories[category]?.numCorrect / test.testSections[0]?.rootCategories[category]?.numQuestions) * 100)) 
                                                            ? '-' 
                                                            : `${Math.round((test.testSections[0]?.rootCategories[category]?.numCorrect / test.testSections[0]?.rootCategories[category]?.numQuestions) * 100)}%`
                                                        : '-'}
                                                </td>
                                                ))}                                                     */}
                                            
                                            </tr>
                                        ))}
                                    </table>
                                    <tr className="historicalReportACT__tableSpacing">
                                            <td colSpan={4}></td> 
                                    </tr>
                                    <table className='historicalReportACT__table'>
                                        <thead>
                                            <tr className='historicalReportACT__tableHeader'>
                                                <th className='historicalReportACT__tableRowName'>Math</th>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((data, i)=>(
                                                    <th>{data.testNumber}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tr className="historicalReportACT__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {mathRootCategories?.map((category)=>(
                                            <tr>
                                                <td className='historicalReportACT__tableRowName'>{category.length>0?category:'-'}</td>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((test, j) => {
                                                    const mathSection = test.testSections.find(section => section.subject === "ACT Mathematics");
                                                    return (
                                                        <td key={j}>
                                                            {mathSection?.rootCategories[category]?.numQuestions > 0 
                                                                ? isNaN(Math.round((mathSection.rootCategories[category]?.numCorrect / mathSection.rootCategories[category]?.numQuestions) * 100)) 
                                                                    ? '-' 
                                                                    : `${Math.round((mathSection.rootCategories[category]?.numCorrect / mathSection.rootCategories[category]?.numQuestions) * 100)}%`
                                                                : '-'}
                                                        </td>
                                                    );
                                                })}
                                                {/* {tests.map((test,j)=>(
                                                <td key={j}>
                                                    {test.testSections[1]?.rootCategories[category]?.numQuestions > 0 
                                                        ? isNaN(Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect / test.testSections[1]?.rootCategories[category]?.numQuestions) * 100)) 
                                                            ? '-' 
                                                            : `${Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect / test.testSections[1]?.rootCategories[category]?.numQuestions) * 100)}%`
                                                        : '-'}
                                                </td>
                                                    // <td>{Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect/test.testSections[1]?.rootCategories[category]?.numQuestions)*100)}%</td>
                                                ))} */}
                                            </tr>
                                        ))}
                                    </table>
                                    <tr className="historicalReportACT__tableSpacing">
                                        <td colSpan={4}></td> 
                                    </tr>
                                    <table className='historicalReportACT__table'>
                                        <thead>
                                            <tr className='historicalReportACT__tableHeader'>
                                                <th className='historicalReportACT__tableRowName'>Reading</th>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((data, i)=>(
                                                    <th>{data.testNumber}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tr className="historicalReportACT__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {readingRootCategories?.map((category)=>(
                                            <tr>
                                                <td className='historicalReportACT__tableRowName'>{category.length>0?category:'-'}</td>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((test, j) => {
                                                    const readingSection = test.testSections.find(section => section.subject === "ACT Reading");
                                                    return (
                                                        <td key={j}>
                                                            {readingSection?.rootCategories[category]?.numQuestions > 0 
                                                                ? isNaN(Math.round((readingSection.rootCategories[category]?.numCorrect / readingSection.rootCategories[category]?.numQuestions) * 100)) 
                                                                    ? '-' 
                                                                    : `${Math.round((readingSection.rootCategories[category]?.numCorrect / readingSection.rootCategories[category]?.numQuestions) * 100)}%`
                                                                : '-'}
                                                        </td>
                                                    );
                                                })}
                                                {/* {tests.map((test,j)=>(
                                                <td key={j}>
                                                    {test.testSections[1]?.rootCategories[category]?.numQuestions > 0 
                                                        ? isNaN(Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect / test.testSections[1]?.rootCategories[category]?.numQuestions) * 100)) 
                                                            ? '-' 
                                                            : `${Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect / test.testSections[1]?.rootCategories[category]?.numQuestions) * 100)}%`
                                                        : '-'}
                                                </td>
                                                    // <td>{Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect/test.testSections[1]?.rootCategories[category]?.numQuestions)*100)}%</td>
                                                ))} */}
                                            </tr>
                                        ))}
                                    </table>
                                    <tr className="historicalReportACT__tableSpacing">
                                        <td colSpan={4}></td> 
                                    </tr>
                                    {tests.some(test => test.testSections.some(section => section.subject === "ACT Science")) &&
                                        <table className='historicalReportACT__table'>
                                            <thead>
                                                <tr className='historicalReportACT__tableHeader'>
                                                    <th className='historicalReportACT__tableRowName'>Science</th>
                                                    {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                        <th key={-idx} /> 
                                                    ))}
                                                    {tests.map((data, i)=>(
                                                        <th>{data.testNumber}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tr className="historicalReportACT__tableSpacingRow">
                                                <td colSpan={4}></td> 
                                            </tr>
                                            {scienceRootCategories?.map((category)=>(
                                                <tr>
                                                    <td className='historicalReportACT__tableRowName'>{category.length>0?category:'-'}</td>
                                                    {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                        <th key={-idx} /> 
                                                    ))}
                                                    {tests.map((test, j) => {
                                                        const scienceSection = test.testSections.find(section => section.subject === "ACT Science");
                                                        return (
                                                            <td key={j}>
                                                                {scienceSection?.rootCategories[category]?.numQuestions > 0 
                                                                    ? isNaN(Math.round((scienceSection.rootCategories[category]?.numCorrect / scienceSection.rootCategories[category]?.numQuestions) * 100)) 
                                                                        ? '-' 
                                                                        : `${Math.round((scienceSection.rootCategories[category]?.numCorrect / scienceSection.rootCategories[category]?.numQuestions) * 100)}%`
                                                                    : '-'}
                                                            </td>
                                                        );
                                                    })}
                                                    {/* {tests.map((test,j)=>(
                                                    <td key={j}>
                                                        {test.testSections[1]?.rootCategories[category]?.numQuestions > 0 
                                                            ? isNaN(Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect / test.testSections[1]?.rootCategories[category]?.numQuestions) * 100)) 
                                                                ? '-' 
                                                                : `${Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect / test.testSections[1]?.rootCategories[category]?.numQuestions) * 100)}%`
                                                            : '-'}
                                                    </td>
                                                        // <td>{Math.round((test.testSections[1]?.rootCategories[category]?.numCorrect/test.testSections[1]?.rootCategories[category]?.numQuestions)*100)}%</td>
                                                    ))} */}
                                                </tr>
                                            ))}
                                        </table>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            :
            <Loader />}
        </div>
    );      
}

export default HistoricalACTReport;